import { useEffect, useState } from 'react';
import { API_CACHE_TTL, fetchCachedGetApi, isAbortError } from '@/utils/apiCache';

interface UseGetAlbumsProps {
  artistId: string | null;
}

interface AlbumData {
  albums: Array<{
    id: string;
    name: string;
    images: Array<{ url: string }>;
    release_date: string;
    album_group: string;
    external_urls: { spotify: string };
    artists: Array<{
      external_urls: { spotify: string };
    }>;
  }>;
  error?: string;
}

function useGetAlbums(
  props: UseGetAlbumsProps,
  options?: any,
  dependencies: any[] = []
): AlbumData | null {
  const [albumData, setAlbumData] = useState<AlbumData | null>(null);
  const { artistId } = props;

  async function fetchSpotifyGetArtistAlbums(artistId: string, signal?: AbortSignal): Promise<AlbumData | null> {
    if (!artistId || artistId === 'undefined') {
      return null;
    }
    if (typeof window !== 'undefined') {
      console.log('calling fetchSpotifyGetArtistAlbums', artistId);
      return fetchCachedGetApi<AlbumData>(`/.netlify/functions/SpotifyGetArtistAlbums`, { artist: artistId }, {
        ttlMs: API_CACHE_TTL.SIX_HOURS,
        shouldCache: (data) => Array.isArray(data?.albums) && !data.error,
        signal,
      });
    }
    return null;
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !artistId || artistId === 'undefined') return;

    const controller = new AbortController();

    fetchSpotifyGetArtistAlbums(artistId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setAlbumData(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        console.log('err:', err);
      });

    return () => controller.abort();
  }, [...dependencies]);

  if (!artistId || artistId === 'undefined') {
    return null;
  }

  return albumData;
}

export default useGetAlbums;
